import { defineStore } from "pinia";
import api from "@/plugins/api";
import {
  User,
  UserState,
  LoginCredentials,
  RegisterData,
  AuthResponse,
} from "@/types/userTypes";
import { useProjectSummaryStore } from "@/stores/project";

export const useAuthStore = defineStore("auth", {
  state: (): UserState => ({
    user: null,
    isAuthenticated: false,
    token: null,
  }),

  getters: {
    currentUser: (state) => state.user,
    isLoggedIn: (state) => state.isAuthenticated,
    userToken: (state) => state.token,
    isStaff: (state) => state.user?.is_staff || false,
    isSuperuser: (state) => state.user?.is_superuser || false,
  },

  actions: {
    async login(credentials: LoginCredentials) {
      try {
        const response = await api.post<AuthResponse>(
          "/auth/login/",
          credentials
        );
        console.log("Login response:", response.data); // Debug log

        // Check if access token exists in response
        if (!response.data.access) {
          console.error("Login response missing access token:", response.data);
          throw new Error("No access token received in login response");
        }

        const { access: token, refresh, user } = response.data;
        console.log("Extracted access token:", token); // Debug log

        // Store tokens and user data
        this.setAuthData(token, user);
        localStorage.setItem("refresh_token", refresh);
        localStorage.setItem("user", JSON.stringify(user));

        // Verify token was stored
        const storedToken = localStorage.getItem("access_token");
        console.log("Verified stored token:", storedToken); // Debug log

        return user;
      } catch (error) {
        console.error("Login failed:", error);
        throw error;
      }
    },

    async register(data: RegisterData) {
      try {
        const response = await api.post<AuthResponse>("/auth/register/", data);
        const { access: token, refresh, user } = response.data;

        this.setAuthData(token, user);
        localStorage.setItem("refresh_token", refresh);
        localStorage.setItem("user", JSON.stringify(user));

        return user;
      } catch (error) {
        console.error("Registration failed:", error);
        throw error;
      }
    },

    setAuthData(token: string, user: User) {
      if (!token) {
        console.error("Attempted to set auth data with no token");
        return;
      }

      this.token = token;
      this.user = user;
      this.isAuthenticated = true;

      // Set token in localStorage
      localStorage.setItem("access_token", token);
      console.log("Token stored:", token); // Debug log

      // Set default authorization header for all future requests
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      console.log("API headers set:", api.defaults.headers.common); // Debug log

      // Initialize Google Maps API after successful authentication
      this.initializeGoogleMapsAfterAuth();
    },

    setUser(user: User | null) {
      this.user = user;
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      } else {
        localStorage.removeItem("user");
      }
    },

    logout() {
      this.user = null;
      this.token = null;
      this.isAuthenticated = false;
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");

      // Clear project summaries
      const projectSummaryStore = useProjectSummaryStore();
      projectSummaryStore.clearProjectSummaries();
    },

    initializeFromStorage() {
      const token = localStorage.getItem("access_token");
      const userStr = localStorage.getItem("user");

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          this.setAuthData(token, user);
        } catch (error) {
          console.error("Failed to initialize auth from storage:", error);
          this.logout();
        }
      }
    },

    async updateUserProfile(profileData: {
      first_name: string;
      last_name: string;
      company_name: string;
    }) {
      try {
        const response = await api.put("/auth/user/update/", profileData);
        if (this.user) {
          this.setUser({
            ...this.user,
            ...response.data,
          });
        }
        return response.data;
      } catch (error) {
        console.error("Failed to update user profile:", error);
        throw error;
      }
    },

    async changePassword(passwordData: {
      old_password: string;
      new_password: string;
      new_password2: string;
    }) {
      try {
        await api.post("/auth/user/change-password/", passwordData);
      } catch (error) {
        console.error("Failed to change password:", error);
        throw error;
      }
    },

    async initializeGoogleMapsAfterAuth() {
      try {
        // Import dynamically to avoid circular dependencies
        const { loadGoogleMapsAPI } = await import("@/utils/googleMapsLoader");
        await loadGoogleMapsAPI();
        console.log("Google Maps API initialized after authentication");
      } catch (error) {
        console.error("Failed to initialize Google Maps API after auth:", error);
      }
    },
  },
});
